// Ported from the native app's ChartAnnotations.swift. Dates are stored as
// epoch milliseconds (JSON-friendly) rather than Date objects.

import { Emitter, onStorageChange, storageGet, storageSet } from "./storage.js";

export interface ChartLinePoint {
  time: number; // epoch ms
  price: number;
}

/**
 * A trendline gets the full feature set (lock, alarm); an arrow is just a
 * solid line with an arrowhead; text is a single-point label. Rectangle and
 * fibonacci reuse the two-point start/end model (opposite corners / swing
 * high-low). longPosition/shortPosition: entry at start.price, take-profit
 * at end.price, stop at stopLossPrice. freeform's real shape is `path`.
 */
export type LineKind =
  | "trend"
  | "arrow"
  | "text"
  | "rectangle"
  | "fibonacci"
  | "longPosition"
  | "shortPosition"
  | "freeform";

export interface ChartLine {
  id: string;
  symbol: string;
  start: ChartLinePoint;
  end: ChartLinePoint;
  isLocked: boolean;
  /** Only meaningful for non-horizontal trend lines — horizontal lines'
   * alarm state lives in WatchlistStore.alerts instead. */
  alarmEnabled: boolean;
  alarmWasAbove?: boolean;
  kind: LineKind;
  text?: string;
  stopLossPrice?: number;
  path?: ChartLinePoint[];
  /** Freeform drawings made of several pen-down strokes ("lifting the pen"
   * while handwriting). When present it supersedes `path`, which is kept
   * as the flattened points for bounds/legacy rendering. */
  strokes?: ChartLinePoint[][];
}

/** Treated as horizontal within a small epsilon (endpoint-drag float drift). */
export function isHorizontal(line: ChartLine): boolean {
  return Math.abs(line.start.price - line.end.price) < 0.0005;
}

/** This line's price at a given time, interpolating/extrapolating by slope. */
export function linePriceAt(line: ChartLine, time: number): number {
  const t0 = line.start.time;
  const t1 = line.end.time;
  if (t1 === t0) return line.start.price;
  const fraction = (time - t0) / (t1 - t0);
  return line.start.price + fraction * (line.end.price - line.start.price);
}

const STORAGE_KEY = "chartDrawnLines";

/**
 * Drawn lines keyed by "<symbol>-<interval>" so each timeframe on each
 * instrument keeps its own independent set. Backed by chrome.storage.local,
 * and kept in sync across contexts (popup, chart window, service worker)
 * via the storage change listener.
 */
export class ChartLineStore {
  linesByChart: Record<string, ChartLine[]> = {};
  readonly changed = new Emitter();

  async init(): Promise<void> {
    this.linesByChart = await storageGet<Record<string, ChartLine[]>>(STORAGE_KEY, {});
    onStorageChange<Record<string, ChartLine[]>>(STORAGE_KEY, (value) => {
      this.linesByChart = value ?? {};
      this.changed.emit();
    });
    this.changed.emit();
  }

  private async persist(): Promise<void> {
    await storageSet(STORAGE_KEY, this.linesByChart);
  }

  lines(chartKey: string): ChartLine[] {
    return this.linesByChart[chartKey] ?? [];
  }

  async addLine(line: ChartLine, chartKey: string): Promise<void> {
    (this.linesByChart[chartKey] ??= []).push(line);
    await this.persist();
    this.changed.emit();
  }

  async updateLine(line: ChartLine, chartKey: string): Promise<void> {
    const lines = this.linesByChart[chartKey];
    if (!lines) return;
    const idx = lines.findIndex((l) => l.id === line.id);
    if (idx < 0) return;
    lines[idx] = line;
    await this.persist();
    this.changed.emit();
  }

  async deleteLine(id: string, chartKey: string): Promise<void> {
    const lines = this.linesByChart[chartKey];
    if (!lines) return;
    this.linesByChart[chartKey] = lines.filter((l) => l.id !== id);
    await this.persist();
    this.changed.emit();
  }

  /** Clears every drawing across all timeframe sections for one symbol. */
  async clearAll(symbol: string): Promise<void> {
    const prefix = `${symbol}-`;
    for (const key of Object.keys(this.linesByChart)) {
      if (key.startsWith(prefix)) this.linesByChart[key] = [];
    }
    await this.persist();
    this.changed.emit();
  }
}

export const chartLineStore = new ChartLineStore();
